import React, { useEffect, useState } from "react";
import api from "../../../api/axios";
import { uploadImage } from "../../../api/media";
import StoreScopePicker, {
  defaultStoreScope,
  storeScopeOf,
} from "../component/StoreScopePicker";

const BASE_URL = process.env.REACT_APP_API_URL;

/* Date -> yyyy-MM-dd for <input type="date"> */
const toDateInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmt = (v) => {
  if (!v) return "Permanent";
  const d = new Date(v);
  return `${String(d.getDate()).padStart(2, "0")}-${String(
    d.getMonth() + 1
  ).padStart(2, "0")}-${d.getFullYear()}`;
};

const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1,
  color: "var(--mly-text-muted)",
  marginBottom: 8,
  textTransform: "uppercase",
};
const inputStyle = {
  width: "100%",
  padding: "11px 14px",
  borderRadius: 10,
  border: "1.5px solid var(--mly-border)",
  fontSize: 14,
  boxSizing: "border-box",
  outline: "none",
};

const Branding = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const [name, setName] = useState("");
  const [accentColor, setAccentColor] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [headerLogo, setHeaderLogo] = useState(null); // File
  const [splashLogo, setSplashLogo] = useState(null); // File
  const [headerPreview, setHeaderPreview] = useState(null);
  const [splashPreview, setSplashPreview] = useState(null);

  /* which stores of the estate this theme applies to */
  const [storeScope, setStoreScope] = useState(defaultStoreScope());

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      /* Branding is one of the two reads that keep Node's wrapper:
         { status, message, data:[...] } — so res.data.data still holds the list. */
      const res = await api.get("/admin/content/branding");
      setItems(res.data?.data || []);
    } catch (e) {
      setError(
        e.response?.data?.message || "Failed to load branding themes"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setName("");
    setAccentColor("");
    setStartDate("");
    setEndDate("");
    setHeaderLogo(null);
    setSplashLogo(null);
    setHeaderPreview(null);
    setSplashPreview(null);
    setStoreScope(defaultStoreScope());
    setFormError("");
  };

  const openAdd = () => {
    setEditItem(null);
    resetForm();
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setName(item.name || "");
    setAccentColor(item.accentColor || "");
    setStartDate(toDateInput(item.startDate));
    setEndDate(toDateInput(item.endDate));
    setHeaderLogo(null);
    setSplashLogo(null);
    setHeaderPreview(
      item.headerLogo ? `${BASE_URL}/uploads/${item.headerLogo}` : null
    );
    setSplashPreview(
      item.splashLogo ? `${BASE_URL}/uploads/${item.splashLogo}` : null
    );
    setStoreScope(storeScopeOf(item));
    setFormError("");
    setShowModal(true);
  };

  const close = () => {
    setShowModal(false);
    setEditItem(null);
    resetForm();
  };

  const pickHeader = (file) => {
    if (file && file.type?.startsWith("image/")) {
      setHeaderLogo(file);
      setHeaderPreview(URL.createObjectURL(file));
    }
  };
  const pickSplash = (file) => {
    if (file && file.type?.startsWith("image/")) {
      setSplashLogo(file);
      setSplashPreview(URL.createObjectURL(file));
    }
  };

  const save = async () => {
    if (!name.trim()) {
      setFormError("Theme name is required");
      return;
    }
    /* the server refuses endDate == startDate (invalid_window), so does the form */
    if (startDate && endDate && new Date(endDate) <= new Date(startDate)) {
      setFormError("End date must be after the start date");
      return;
    }

    setSaving(true);
    setFormError("");

    /* JSON, not multipart: /admin/content/branding takes fields, never files.
       startDate/endDate are sent even when blank — the server clears the
       schedule for any key that is present, which is how "permanent" is set. */
    const payload = {
      name,
      accentColor: accentColor || "",
      startDate: startDate || "",
      endDate: endDate || "",
    };

    // TODO(cutover): server does not store store scope for themes —
    // shop.CfgAppBranding has no store columns, so availableInAllStores / stores /
    // excludedStores were accepted and dropped on write and are absent on read.
    // They are no longer sent; a store account's write is refused outright (403
    // estate_wide).

    try {
      /* headerLogo/splashLogo are RELATIVE uploads paths ("branding/diwali.webp").
         A picked file goes up through /admin/media/branding first; an absent key keeps
         the logo the theme already has. */
      if (headerLogo) payload.headerLogo = await uploadImage(headerLogo, "branding");
      if (splashLogo) payload.splashLogo = await uploadImage(splashLogo, "branding");
      if (editItem) {
        await api.put(`/admin/content/branding/${editItem.id}`, payload);
      } else {
        await api.post(`/admin/content/branding`, payload);
      }
      await load();
      close();
    } catch (e) {
      setFormError(e.response?.data?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (id) => {
    try {
      await api.patch(`/admin/content/branding/${id}/toggle`);
      load();
    } catch (e) {
      alert(e.response?.data?.message || "Failed to toggle");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this theme?")) return;
    try {
      /* a row that is already gone now answers 404, so this lands in catch
         instead of Node's cheerful 200 */
      await api.delete(`/admin/content/branding/${id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.message || "Failed to delete");
    }
  };

  const dropZone = (preview, onPick, id, hint) => (
    <div
      onClick={() => document.getElementById(id).click()}
      style={{
        border: "2px dashed var(--mly-heading)",
        borderRadius: 12,
        background: preview ? "var(--mly-surface)" : "var(--mly-accent-soft)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 96,
        cursor: "pointer",
        overflow: "hidden",
        padding: 8,
      }}
    >
      {preview ? (
        <img
          src={preview}
          alt="preview"
          style={{ maxHeight: 72, maxWidth: "100%", objectFit: "contain" }}
        />
      ) : (
        <>
          <div style={{ fontSize: 22 }}>🖼️</div>
          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--mly-text-soft)" }}>
            {hint}
          </span>
          <span style={{ fontSize: 11, color: "var(--mly-text-muted)" }}>PNG, JPG, WEBP</span>
        </>
      )}
      <input
        id={id}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={(e) => {
          onPick(e.target.files[0]);
          e.target.value = "";
        }}
        style={{ display: "none" }}
      />
    </div>
  );

  return (
    <div>
      <div className="container-fluid">
        <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>
          <div className="card-header d-flex justify-content-between align-items-center">
            <div>
              <h5 style={{ margin: 0, fontWeight: 700 }}>App Branding</h5>
              <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
                Schedule the in-app logo by date. The active theme re-skins the
                app; blank dates = permanent.
              </span>
            </div>
            <button
              className="btn btn-primary btn-sm widthbtn"
              onClick={openAdd}
            >
              + Add
            </button>
          </div>

          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : error ? (
              <div className="text-danger text-center py-4">{error}</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="display table table-bordered">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 60 }}>#</th>
                      <th style={{ minWidth: 110 }}>Header Logo</th>
                      <th style={{ minWidth: 160 }}>Name</th>
                      <th style={{ minWidth: 130 }}>From</th>
                      <th style={{ minWidth: 130 }}>To</th>
                      <th style={{ minWidth: 90 }}>Active</th>
                      <th style={{ minWidth: 110 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          style={{
                            textAlign: "center",
                            padding: "40px 0",
                            color: "var(--mly-text-muted)",
                          }}
                        >
                          No themes yet — add one to schedule a seasonal look.
                        </td>
                      </tr>
                    ) : (
                      items.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td>
                            <img
                              src={
                                item.headerLogo
                                  ? `${BASE_URL}/uploads/${item.headerLogo}`
                                  : "/assets/images/default.png"
                              }
                              alt=""
                              style={{
                                height: 40,
                                maxWidth: 100,
                                objectFit: "contain",
                                background: "var(--mly-surface-2)",
                                borderRadius: 8,
                                border: "1px solid var(--mly-border)",
                                padding: 4,
                              }}
                            />
                          </td>
                          <td>{item.name}</td>
                          <td>{fmt(item.startDate)}</td>
                          <td>{fmt(item.endDate)}</td>
                          <td>
                            <label className="switch">
                              <input
                                type="checkbox"
                                checked={item.status}
                                onChange={() => toggle(item.id)}
                              />
                              <span className="slider round"></span>
                            </label>
                          </td>
                          <td className="d-flex gap-2">
                            <button
                              onClick={() => openEdit(item)}
                              title="Edit"
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                border: "none",
                                background: "var(--mly-accent-soft)",
                                color: "var(--mly-heading)",
                                cursor: "pointer",
                              }}
                            >
                              ✎
                            </button>
                            <button
                              onClick={() => remove(item.id)}
                              title="Delete"
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                border: "none",
                                background: "#fff5f5",
                                color: "#ef4444",
                                cursor: "pointer",
                              }}
                            >
                              🗑
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <>
          <div
            onClick={close}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(2px)",
              zIndex: 1040,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1050,
              width: "100%",
              maxWidth: 520,
              background: "var(--mly-surface)",
              borderRadius: 20,
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
              padding: "28px 32px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 20,
              }}
            >
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
                {editItem ? "Edit Theme" : "New Theme"}
              </h2>
              <button
                onClick={close}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "1px solid var(--mly-border)",
                  background: "var(--mly-surface-2)",
                  cursor: "pointer",
                  fontSize: 16,
                }}
              >
                ×
              </button>
            </div>

            {formError && (
              <div
                style={{
                  background: "#fef2f2",
                  color: "#dc2626",
                  padding: "10px 14px",
                  borderRadius: 8,
                  marginBottom: 16,
                  fontSize: 13,
                }}
              >
                {formError}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>
                Theme Name<span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Diwali 2026"
                style={inputStyle}
              />
            </div>

            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Header Logo</label>
                {dropZone(
                  headerPreview,
                  pickHeader,
                  "brandingHeaderInput",
                  "UPLOAD LOGO"
                )}
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Splash Logo (optional)</label>
                {dropZone(
                  splashPreview,
                  pickSplash,
                  "brandingSplashInput",
                  "UPLOAD SPLASH"
                )}
              </div>
            </div>

            {(headerLogo || splashLogo) && (
              <div style={{ fontSize: 12, color: "var(--mly-text-muted)", marginBottom: 16 }}>
                New logos are uploaded when you save.
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>
                Accent Colour (optional)
              </label>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <input
                  type="color"
                  value={accentColor || "#0E3A2C"}
                  onChange={(e) => setAccentColor(e.target.value)}
                  style={{
                    width: 46,
                    height: 40,
                    border: "1.5px solid var(--mly-border)",
                    borderRadius: 8,
                    background: "var(--mly-surface)",
                    cursor: "pointer",
                  }}
                />
                <input
                  type="text"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  placeholder="#0E3A2C"
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>
                Display Window
                <span
                  style={{
                    fontWeight: 500,
                    letterSpacing: 0,
                    textTransform: "none",
                    color: "var(--mly-text-muted)",
                    marginLeft: 6,
                  }}
                >
                  (blank = permanent)
                </span>
              </label>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <span
                    style={{ fontSize: 11, color: "var(--mly-text-muted)", display: "block", marginBottom: 4 }}
                  >
                    From
                  </span>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <span
                    style={{ fontSize: 11, color: "var(--mly-text-muted)", display: "block", marginBottom: 4 }}
                  >
                    To
                  </span>
                  <input
                    type="date"
                    value={endDate}
                    min={startDate || undefined}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>

            {/* TODO(cutover): server does not store store scope for themes —
                the picker's value is not sent (see save()). */}
            <div
              style={{
                fontSize: 12,
                color: "var(--mly-text-muted)",
                marginBottom: 8,
              }}
            >
              A theme applies to every store — store scope is not saved yet.
            </div>
            <StoreScopePicker value={storeScope} onChange={setStoreScope} />

            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={close}
                style={{
                  padding: "11px 24px",
                  borderRadius: 10,
                  border: "1.5px solid var(--mly-border)",
                  background: "var(--mly-surface-2)",
                  color: "var(--mly-text-soft)",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: "11px 24px",
                  borderRadius: 10,
                  border: "none",
                  background: saving ? "#a5a4f3" : "var(--mly-heading)",
                  color: "var(--mly-on-accent)",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                {saving ? "Saving..." : editItem ? "Update Theme" : "Save Theme"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Branding;
